import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Close as ErrorIcon,
} from '@mui/icons-material';

const TOOL_DISPLAY = {
  memory: { label: 'Project Memory', color: '#5f8a8f' },
  save_artifact: { label: 'Save Artifact', color: '#5f8a8f' },
  update_artifact: { label: 'Update Artifact', color: '#5f8a8f' },
  ask_user: { label: 'Ask User', color: '#FF9933' },
  web_search: { label: 'Web Search', color: '#4DBCF3' },
  web_fetch: { label: 'Web Fetch', color: '#4DBCF3' },
  get_current_information: { label: 'Search', color: '#4DBCF3' },
  // SDK generation progress events (designs, design-system, wireframes)
  gather_context: { label: 'Gathering context', color: '#5f8a8f' },
  start_generation: { label: 'Starting generation', color: '#4DBCF3' },
  create_file: { label: 'Creating file', color: '#2d9d5e' },
  verify_pages: { label: 'Verifying interactivity', color: '#FF9933' },
  finalize: { label: 'Finalizing', color: '#5f8a8f' },
};

const StatusIndicator = ({ status }) => {
  if (status === 'running') {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#66CC33',
          mr: 1,
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.4 },
          },
        }}
      />
    );
  }
  if (status === 'done') {
    return (
      <CheckIcon
        sx={{ fontSize: 14, color: '#2d9d5e', mr: 0.5, verticalAlign: 'middle' }}
      />
    );
  }
  if (status === 'error') {
    return (
      <ErrorIcon
        sx={{ fontSize: 14, color: '#fa5b5b', mr: 0.5, verticalAlign: 'middle' }}
      />
    );
  }
  return null;
};

/**
 * ToolActivityBlock — displays tool usage activity inline in the chat.
 * Styled like Claude Code CLI: green dot + bold name + collapsible detail.
 *
 * Props:
 *   tools: Array of { name, status, detail?, summary? }
 */
const ToolActivityBlock = ({ tools }) => {
  const [expanded, setExpanded] = useState(false);

  if (!tools || tools.length === 0) return null;

  // Determine the overall status based on individual tool statuses
  const hasRunning = tools.some(t => t.status === 'running');
  const hasError = tools.some(t => t.status === 'error');
  const hasDetails = tools.some(t => t.detail || t.summary);

  return (
    <Box
      sx={{
        my: 1,
        py: 0.75,
        px: 1.5,
        borderLeft: '3px solid',
        borderColor: hasError ? '#fa5b5b' : hasRunning ? '#66CC33' : '#2d9d5e',
        backgroundColor: 'rgba(95, 138, 143, 0.04)',
        borderRadius: '0 6px 6px 0',
        fontFamily: '"Sofia Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Tool names row */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {tools.map((tool, idx) => {
            const display = TOOL_DISPLAY[tool.name] || { label: tool.name, color: '#475969' };
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator status={tool.status} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#3A4754',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {display.label}
                </Typography>
                {(tool.detail || tool.summary) && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      color: '#6B757F',
                      ml: 0.75,
                      fontWeight: 400,
                    }}
                  >
                    {tool.status === 'done' || tool.status === 'error'
                      ? tool.summary
                      : tool.detail}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {hasDetails && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              width: 24,
              height: 24,
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              color: '#8D979F',
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* Collapsible details */}
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: '1px solid #E3E6E8',
          }}
        >
          {tools.map((tool, idx) => (
            <Box key={idx} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  color: '#8D979F',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {tool.detail && <span>→ {tool.detail}</span>}
                {tool.summary && (
                  <span style={{ display: 'block', color: tool.status === 'error' ? '#fa5b5b' : '#2d9d5e' }}>
                    ← {tool.summary}
                  </span>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ToolActivityBlock;
